<template>
  <ul :class="b('', { variant })">
    <li
      v-for="(item, index) in navigationItems"
      :key="item.label"
      :class="b('menu-item', { active: isActiveIndex(index) })"
    >
      <component
        :is="getTopLevelHtmlTag(item)"
        :href="item.uri ? item.uri : ''"
        :target="item.target ? item.target : '_self'"
        :class="b('menu-link', { hasSubmenu: !!item.pages?.length })"
        @click="onSelectMenu(index)"
      >
        {{ item.label }}
      </component>

      <div
        v-if="item.pages?.length"
        :class="b('dropdown', { active: isActiveIndex(index) })"
      >
        <ul :class="b('dropdown-menu')">
          <li
            v-for="subItem in item.pages"
            :key="subItem.label"
            :class="b('dropdown-item')"
          >
            <a
              :href="subItem.uri ? subItem.uri : ''"
              :target="subItem.target ? subItem.target : '_self'"
              :class="b('dropdown-link')"
            >
              {{ subItem.label }}
            </a>
          </li>
        </ul>
      </div>
    </li>
  </ul>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import { NavigationItem } from '@/types/navigation';

  type Data = {
    selectedSubMenu: number | null;
  };

  export default defineComponent({
    name: 'c-main-navigation',

    // components: {},

    props: {
      /**
       * A list of navigation items.
       */
      navigationItems: {
        type: Array as PropType<NavigationItem[]>,
        default: () => [],
      },

      /**
       * Define a display variant.
       */
      variant: {
        type: String,
        default: 'desktop',
        validator: (value: string) => ['desktop', 'mobile'].includes(value),
      },
    },
    data(): Data {
      return {
        selectedSubMenu: null,
      };
    },

    computed: {
      isMobileVariant(): boolean {
        return this.variant === 'mobile';
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      getTopLevelHtmlTag(item: NavigationItem): string {
        if (item?.pages?.length && this.isMobileVariant) {
          return 'span';
        }

        return item?.uri ? 'a' : 'span';
      },

      isActiveIndex(index: number): boolean {
        return this.selectedSubMenu !== null && this.selectedSubMenu === index;
      },

      onSelectMenu(index: number) {
        if (this.isMobileVariant) {
          this.selectedSubMenu = this.isActiveIndex(index) ? null : index;
        }
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-main-navigation {
    $this: &;

    &--variant-desktop {
      #{$this}__menu-item {
        height: 100%;
        position: relative;
        cursor: default;

        &:hover,
        &:focus-within {
          #{$this}__menu-link {
            background: var(--theme-color-grayscale--200);
            color: var(--theme-color-grayscale--800);
          }

          #{$this}__dropdown {
            display: flex;
          }
        }
      }

      #{$this}__menu-link {
        display: flex;
        align-items: center;
        text-align: center;
        height: 100%;
        padding: 0 1.5rem;
        color: var(--theme-color-grayscale--1000);
        font-size: variables.$font-size--big;
        line-height: 1.333rem;

        &:hover {
          background: var(--theme-color-grayscale--200);
          color: var(--theme-color-grayscale--800);
        }
      }

      #{$this}__dropdown {
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        background: var(--theme-color-grayscale--900);
        box-shadow: 0 5px 15px rgba(var(--theme-color-grayscale--200-rgb), 0.9);
        min-width: 24rem;
        z-index: 101;
      }

      #{$this}__dropdown-menu {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      #{$this}__dropdown-link {
        display: flex;
        width: 100%;
        padding: 0.75rem 1.5rem;
        text-decoration: none;
        color: var(--theme-font-color--text);

        &:hover {
          background: var(--theme-color-grayscale--800);
          color: var(--theme-color-grayscale--400);
        }
      }
    }

    &--variant-mobile {
      #{$this}__menu-link {
        position: relative;
        color: var(--theme-color-grayscale--1000);
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 1.5rem;
        text-decoration: none;
        border-bottom: 1px solid var(--theme-color-grayscale--300);

        &--has-submenu {
          padding-right: 3rem;

          &::after {
            content: '';
            position: absolute;
            display: block;
            inset: 0 15px 0 auto;
            width: 1.5rem;
            background-color: var(--theme-color-grayscale--1000);
            mask-image: url('@/assets/icons/i-chevron--down.svg');
            mask-size: 0.75rem;
            mask-repeat: no-repeat;
            mask-position: center;
          }
        }
      }

      #{$this}__menu-item:hover,
      #{$this}__menu-item--active {
        #{$this}__menu-link {
          cursor: pointer;
          background: var(--theme-color-grayscale--150);
        }
      }

      #{$this}__menu-item--active {
        #{$this}__menu-link--has-submenu:after {
          transform: rotate(180deg);
        }
      }

      #{$this}__dropdown {
        display: none;
        width: 100%;
        z-index: 101;
        background-color: var(--theme-color-grayscale--200);

        &--active {
          display: block;
        }
      }

      #{$this}__dropdown-menu {
        display: flex;
        width: 100%;
        flex-direction: column;
        margin: 0;
      }

      #{$this}__dropdown-item {
        display: block;
        align-items: center;
        list-style-type: none;
        list-style-position: outside;
        margin: 0;
        padding: 0;
        border-bottom: 1px solid var(--theme-color-primary--1);
      }

      #{$this}__dropdown-link {
        display: flex;
        width: 100%;
        text-decoration: none;
        padding: 1.5rem 3rem;
        color: var(--theme-color-grayscale--1000);

        &:hover {
          background-color: var(--theme-color-grayscale--650);
          color: var(--theme-color-primary--1);
        }
      }
    }
  }
</style>
